import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Container, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { OpenInNew, Print } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx, filterActionButtonSx } from "../theme/filterStyles";
import {
  contractsService,
  ContractSituation,
  CONTRACT_SITUATION_LABELS,
  PatientContractsReportRow,
} from "../services/contractsService";

/**
 * Relatório Pacientes × contratos (item 66): todo paciente ativo com a situação do contrato que
 * vale para ele — vigente, vencido, futuro ou sem contrato — com vigência inicial e final. O filtro
 * por modelo é calculado no servidor: com um modelo escolhido, "sem contrato" passa a significar
 * "sem ESTE contrato", que é a pergunta de quem vai renovar um pacote.
 */

const formatDate = (iso: string | null) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

const SITUATION_STYLE: Record<ContractSituation, { bg: string; fg: string }> = {
  vigente: { bg: colors.successSoft, fg: colors.successHover },
  vencido: { bg: colors.errorSoft, fg: colors.errorHover },
  futuro: { bg: colors.infoSoft, fg: "#0d47a1" },
  sem_contrato: { bg: colors.backgroundAlt, fg: colors.textSecondary },
};

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
};

const ReportPatientContracts: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [templateId, setTemplateId] = useState("");
  const [situation, setSituation] = useState("");
  const [expiresUntil, setExpiresUntil] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState<PatientContractsReportRow[]>([]);
  const [templateOptions, setTemplateOptions] = useState<{ id: string; title: string }[]>([]);
  const [referenceDate, setReferenceDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsService.patientsReport(templateId || undefined);
      setRows(data.rows);
      setReferenceDate(data.referenceDate);
      if (!templateId) {
        // Os modelos do filtro são os que aparecem nos contratos (inclui modelo inativo ou arquivado)
        const map = new Map<string, string>();
        data.rows.forEach((r) => { if (r.templateId && r.templateTitle) map.set(r.templateId, r.templateTitle); });
        setTemplateOptions(Array.from(map.entries()).map(([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title)));
      }
    } catch {
      setError("Não foi possível carregar o relatório.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    document.title = "Clinic4Us - Pacientes × contratos";
    load();
  }, [load]);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return rows.filter((r) => {
      if (situation && r.situation !== situation) return false;
      if (expiresUntil && (!r.validTo || r.validTo > expiresUntil)) return false;
      if (term && !normalize(`${r.patientName} ${r.responsibleName ?? ""} ${r.templateTitle ?? ""}`).includes(term)) return false;
      return true;
    });
  }, [rows, situation, expiresUntil, search]);

  const summary = useMemo(() => {
    const count = (s: ContractSituation) => rows.filter((r) => r.situation === s).length;
    const soon = rows.filter((r) => r.situation === "vigente" && r.daysToExpire !== null && r.daysToExpire <= 30).length;
    return { current: count("vigente"), soon, expired: count("vencido"), none: count("sem_contrato") };
  }, [rows]);

  const hasFilterChanges = templateId !== "" || situation !== "" || expiresUntil !== "" || search !== "";
  const clearFilters = () => { setTemplateId(""); setSituation(""); setExpiresUntil(""); setSearch(""); };

  const openPatient = (patientId: string) => {
    const clinic = new URLSearchParams(window.location.search).get("clinic") || "";
    window.open(`${window.location.origin}/?page=patient-register&id=${patientId}${clinic ? `&clinic=${clinic}` : ""}`, "_blank");
  };

  const daysLabel = (r: PatientContractsReportRow): { text: string; color: string } => {
    if (r.situation === "sem_contrato") return { text: "—", color: colors.textSecondary };
    if (r.daysToExpire === null) return { text: "indeterminado", color: colors.textSecondary };
    if (r.daysToExpire < 0) return { text: `há ${-r.daysToExpire} dias`, color: colors.errorHover };
    if (r.daysToExpire <= 30) return { text: `em ${r.daysToExpire} dias`, color: colors.warningSoftText };
    return { text: `em ${r.daysToExpire} dias`, color: colors.textPrimary };
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: colors.background }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main c4u-print-page">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Pacientes × contratos</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Quem tem contrato vigente, quem está vencido e quem não tem, com a vigência de cada um.
                {referenceDate ? ` Referência: ${formatDate(referenceDate)}.` : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} className="no-print">
              <Tooltip title="Imprimir" arrow>
                <IconButton onClick={() => window.print()} sx={filterActionButtonSx} aria-label="Imprimir relatório">
                  <Print />
                </IconButton>
              </Tooltip>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField select size="small" label="Contrato (modelo)" value={templateId} onChange={(e) => setTemplateId(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="">Qualquer contrato</MenuItem>
                  {templateOptions.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Situação" value={situation} onChange={(e) => setSituation(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="">Todas</MenuItem>
                  {(Object.keys(CONTRACT_SITUATION_LABELS) as ContractSituation[]).map((s) => <MenuItem key={s} value={s}>{CONTRACT_SITUATION_LABELS[s]}</MenuItem>)}
                </TextField>
                <TextField type="date" size="small" label="Vence até" value={expiresUntil} onChange={(e) => setExpiresUntil(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Buscar" placeholder="Paciente ou responsável" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 100 }} />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
              </div>
            </div>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
              {[
                { label: "Vigentes", valor: summary.current, cor: colors.successHover, nota: templateId ? "com este contrato" : "com contrato em vigor" },
                { label: "Vencem em 30 dias", valor: summary.soon, cor: summary.soon > 0 ? "#fd7e14" : colors.textMuted, nota: "para renovar" },
                { label: "Vencidos", valor: summary.expired, cor: summary.expired > 0 ? colors.error : colors.textMuted, nota: "sem contrato válido" },
                { label: "Sem contrato", valor: summary.none, cor: summary.none > 0 ? colors.textSecondary : colors.textMuted, nota: templateId ? "nunca tiveram este" : "nunca emitido" },
              ].map((k) => (
                <Paper key={k.label} elevation={0} sx={cardSx}>
                  <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{k.label}</Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, color: k.cor, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>{loading ? "…" : k.valor}</Typography>
                  <Typography sx={{ fontSize: 11, color: colors.textMuted }}>{k.nota}</Typography>
                </Paper>
              ))}
            </Box>

            {error && <Typography sx={{ color: colors.error, mb: 2 }}>{error}</Typography>}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: colors.primary }} /></Box>
            ) : (
              <>
                <ResultCount shown={filtered.length} total={rows.length} />
                <Box className="c4u-table__header">
                  <Box className="admin-plans-header-cell" sx={{ flex: "2 1 220px", minWidth: 0 }}>Paciente</Box>
                  <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 160px", minWidth: 0 }}>Responsável</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 120px" }}>Situação</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "2 1 200px", minWidth: 0 }}>Contrato</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Início</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Fim</Box>
                  <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 120px" }}>Vence</Box>
                  <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 56px", justifyContent: "flex-end" }}>Abrir</Box>
                </Box>
                {filtered.length === 0 && (
                  <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 4, textAlign: "center" }}>Nenhum paciente no filtro.</Typography>
                )}
                {filtered.map((r) => {
                  const style = SITUATION_STYLE[r.situation];
                  const days = daysLabel(r);
                  return (
                    <Box key={r.patientId} className="c4u-table__row">
                      <Typography className="admin-plans-cell" data-label="Paciente" sx={{ flex: "2 1 220px", minWidth: 0, fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary, wordBreak: "break-word" }}>{r.patientName}</Typography>
                      <Typography className="admin-plans-cell c4u-col--lg" data-label="Responsável" sx={{ flex: "1 1 160px", minWidth: 0, fontSize: "0.85rem", color: colors.textSecondary, wordBreak: "break-word" }}>{r.responsibleName || "—"}</Typography>
                      <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 120px" }}>
                        <Chip label={CONTRACT_SITUATION_LABELS[r.situation]} size="small" sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, backgroundColor: style.bg, color: style.fg }} />
                      </Box>
                      <Box className="admin-plans-cell" data-label="Contrato" sx={{ flex: "2 1 200px", minWidth: 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.85rem", color: colors.textPrimary, wordBreak: "break-word" }}>
                            {r.templateTitle ? `${r.templateTitle} · v${r.templateVersion}` : "—"}
                          </Typography>
                          {r.requiresSignature ? (
                            <Typography sx={{ fontSize: "0.72rem", color: r.signatureStatus === "signed" ? colors.successHover : colors.warningSoftText }}>
                              {r.signatureStatus === "signed" ? "assinado" : "assinatura pendente"}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                      <Typography className="admin-plans-cell" data-label="Início" sx={{ flex: "0 0 100px", fontSize: "0.85rem", fontVariantNumeric: "tabular-nums" }}>{formatDate(r.validFrom)}</Typography>
                      <Typography className="admin-plans-cell" data-label="Fim" sx={{ flex: "0 0 100px", fontSize: "0.85rem", fontVariantNumeric: "tabular-nums" }}>{r.validFrom ? (r.validTo ? formatDate(r.validTo) : "indeterminado") : "—"}</Typography>
                      <Typography className="admin-plans-cell c4u-col--md" data-label="Vence" sx={{ flex: "0 0 120px", fontSize: "0.85rem", fontWeight: 600, color: days.color }}>{days.text}</Typography>
                      <Box className="admin-plans-cell admin-plans-actions no-print" data-label="Abrir" sx={{ flex: "0 0 56px", display: "flex", justifyContent: "flex-end" }}>
                        <Tooltip title="Abrir o cadastro do paciente" arrow>
                          <IconButton className="c4u-row-action" size="small" onClick={() => openPatient(r.patientId)} sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover }, color: colors.white }} aria-label={`Abrir ${r.patientName}`}>
                            <OpenInNew sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </div>
        </Container>
      </Box>
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPatientContracts;
